import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSearchParams } from 'react-router';

import { useCustomTableContext } from '@/components/table/context/CustomTable.context';
import {
  CustomTableStateDataModel,
  CustomTableStateModel,
} from '@/components/table/model/CustomTableState.model.ts';
import { useCustomTableInitialParamsSetter } from '@/components/table/util/useCustomTableInitialParamsSetter.util.ts';
import { LocalStorageKey, localStorageUtil } from '@/utils/localStorage/localStorage.util.ts';

interface Props<
  SORT_BY_COLUMN_NAME_MODEL extends string,
  SEARCH_FORM_MODEL extends object,
  ROW_ITEM_MODEL extends object,
> {
  init: (
    initialParams: CustomTableStateModel<
      SORT_BY_COLUMN_NAME_MODEL,
      SEARCH_FORM_MODEL,
      ROW_ITEM_MODEL
    >,
  ) => Promise<CustomTableStateDataModel<ROW_ITEM_MODEL>>;
  initParams: CustomTableStateModel<SORT_BY_COLUMN_NAME_MODEL, SEARCH_FORM_MODEL, ROW_ITEM_MODEL>;
  tableKey: string;
  urlStateEnabled?: boolean;
}

export const CustomTableController = <
  SORT_BY_COLUMN_NAME_MODEL extends string,
  SEARCH_FORM_MODEL extends object,
  ROW_ITEM_MODEL extends object,
>({
  init,
  initParams,
  tableKey,
  urlStateEnabled = true,
}: Props<SORT_BY_COLUMN_NAME_MODEL, SEARCH_FORM_MODEL, ROW_ITEM_MODEL>) => {
  const [isTableInitialized, setIsTableInitialized] = useState(false);
  const formMethods = useFormContext();
  const [searchParams] = useSearchParams();
  const { customTableInitialParamsSetter } = useCustomTableInitialParamsSetter(urlStateEnabled);
  const { customTableState, setCustomTableState } = useCustomTableContext();
  type CustomTableStateModelInner = CustomTableStateModel<
    SORT_BY_COLUMN_NAME_MODEL,
    SEARCH_FORM_MODEL,
    ROW_ITEM_MODEL
  >;

  const initFn = async () => {
    const initialParams = structuredClone(initParams);
    initialParams.params = parseDates(initParams.params);
    const tableParams =
      (urlStateEnabled ? searchParams.get(tableKey) : null) ??
      localStorageUtil.getItem<string>({ key: LocalStorageKey.CUSTOM_TABLE, subKey: tableKey });
    if (tableParams) {
      const parsedTableParams = parseStoredTableParams(tableParams, tableKey);
      if (parsedTableParams) {
        Object.entries(parsedTableParams).forEach(([key, value]) => {
          const parsedKey = key as keyof CustomTableStateModelInner;
          if (parsedKey === 'params') {
            initialParams[parsedKey] = JSON.parse(value);
            Object.entries(initialParams[parsedKey]).forEach(([innerKey, innerValue]) => {
              if (typeof innerValue !== 'boolean' && dayjs(innerValue).isValid()) {
                // @ts-expect-error: proper key
                initialParams[parsedKey][innerKey] = dayjs(innerValue);
              }
            });
            return;
          }

          if (parsedKey === 'page') {
            initialParams[parsedKey] = Number(value) - 1;
            return;
          }

          if (parsedKey === 'pageSize') {
            initialParams[parsedKey] = Number(value);
            return;
          }
          initialParams[parsedKey] = value as keyof CustomTableStateModelInner[typeof parsedKey];
        });
      }
    }
    const data = await init(initialParams);
    setIsTableInitialized(true);
    setCustomTableState(cur => ({
      ...cur,
      ...initialParams,
      data: {
        ...data,
      },
    }));
    formMethods.reset(initialParams.params);
  };

  useEffect(() => {
    void initFn();
  }, []);

  useEffect(() => {
    if (!isTableInitialized) {
      return;
    }
    const newTableParams: Record<string, string> = {};
    Object.entries(customTableState).forEach(([key]) => {
      const parsedKey = key as keyof CustomTableStateModel<
        SORT_BY_COLUMN_NAME_MODEL,
        SEARCH_FORM_MODEL,
        ROW_ITEM_MODEL
      >;
      if (parsedKey === 'data') {
        return;
      }

      if (parsedKey === 'page') {
        newTableParams[parsedKey] = (customTableState[parsedKey] + 1).toString();
        return;
      }

      if (parsedKey === 'params') {
        const newParams: Record<string, unknown> = {};
        Object.entries(customTableState[parsedKey]).forEach(([key, value]) => {
          if (typeof value === 'string' && dayjs(value).isValid()) {
            newParams[key] = dayjs(value).toISOString();
          } else {
            newParams[key] = value;
          }
        });
        newTableParams[parsedKey] = JSON.stringify(newParams);
        return;
      }

      newTableParams[parsedKey] = customTableState[parsedKey].toString();
    });
    customTableInitialParamsSetter({
      tableSubKey: tableKey,
      value: JSON.stringify(newTableParams),
    });
  }, [customTableState, tableKey, isTableInitialized]);

  return <></>;
};

const parseDates = <T extends object>(params: T): T => {
  const copy = structuredClone(params);
  Object.entries(params).forEach(([key, value]) => {
    if (value && typeof value !== 'boolean' && dayjs(value).isValid()) {
      // @ts-expect-error: proper key of params
      copy[key] = dayjs(value).clone();
    }
  });
  return copy;
};

function parseStoredTableParams(tableParams: string | null, tableKey: string) {
  if (!tableParams) return null;
  try {
    const parsed = JSON.parse(tableParams) as Record<string, string>;
    return parsed;
  } catch (error) {
    console.warn('Failed to parse table params from storage:', error);
    localStorageUtil.clearItem({ key: LocalStorageKey.CUSTOM_TABLE, subKey: tableKey });
    return null;
  }
}
